import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'
import classNames from 'classnames'
import type { FormikErrors, FormikTouched, FormikValues } from 'formik'
import { useField } from 'formik'
import { get } from 'lodash'
import { useMemo } from 'react'
import { useAvailableCountries } from '@haus-storefront-react/hooks'
import { useTranslation } from 'react-i18next'
import inputStyles from '../input/Input.module.scss'
import styles from './SelectCountry.module.scss'

interface SelectCountryProps {
  name: string
  label: string
  required?: boolean
  width?: number
  errors?: FormikErrors<FormikValues>
  touched?: FormikTouched<FormikValues>
}

export const SelectCountry = ({
  name,
  label,
  required,
  width,
  errors,
  touched,
}: SelectCountryProps) => {
  const { t } = useTranslation()
  const { data: countries, isLoading } = useAvailableCountries()
  const [{ value }, , { setTouched, setValue }] = useField<string>(name)
  const inputId = `${name}`
  const errorId = `${name}-error`

  const options = useMemo(() => {
    return (
      countries
        ?.slice()
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .map((country) => ({ label: country.name, value: country.code })) ?? []
    )
  }, [countries])

  const { hasError, errorMessage } = useMemo(() => {
    const isTouched = Boolean(get(touched, name))
    const message = get(errors, name)
    return {
      hasError: Boolean(isTouched && message),
      errorMessage: typeof message === 'string' ? message : undefined,
    }
  }, [errors, touched, name])

  const fieldStyle =
    typeof width === 'number'
      ? {
          width: `${width}%`,
          flex: `0 0 ${width}%`,
        }
      : undefined

  return (
    <div className={inputStyles.field} style={fieldStyle}>
      <div className={inputStyles.fieldWrapper}>
        <label htmlFor={name} className={inputStyles.label}>
          {label}
          {required ? <span className={inputStyles.required}> *</span> : null}
        </label>

        <Select.Root
          value={typeof value === 'string' ? value : ''}
          onValueChange={(nextValue) => setValue(nextValue)}
          disabled={isLoading}
        >
          <Select.Trigger
            id={inputId}
            className={classNames(inputStyles.input, styles.trigger, {
              [inputStyles.error]: hasError,
            })}
            aria-label={label}
            aria-required={Boolean(required)}
            aria-invalid={Boolean(hasError)}
            aria-describedby={hasError && errorMessage ? errorId : undefined}
            onBlur={() => setTouched(true)}
          >
            <Select.Value
              className={styles.value}
              placeholder={
                isLoading ? t('general.loading') : t('checkout.validation.countryRequired')
              }
            />
            <Select.Icon aria-hidden="true">
              <ChevronDownIcon width={16} height={16} />
            </Select.Icon>
          </Select.Trigger>

          <Select.Content
            className={styles.dropdownContent}
            position="popper"
            sideOffset={0}
            align="start"
            aria-label={t('a11y.checkout.aria.countryOptions')}
          >
            <Select.Viewport className={styles.selectViewport}>
              {options.map((opt) => (
                <Select.Item
                  key={opt.value}
                  value={opt.value}
                  className={styles.selectItem}
                  aria-label={opt.label}
                >
                  <div className={styles.checkIconSpacer}>
                    <Select.ItemIndicator className={styles.itemIndicator} aria-hidden="true">
                      <CheckIcon width={16} height={16} />
                    </Select.ItemIndicator>
                  </div>
                  <Select.ItemText>{opt.label}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      </div>

      {hasError && errorMessage ? (
        <div id={errorId} className={inputStyles.error} role="alert">
          {errorMessage}
        </div>
      ) : null}
    </div>
  )
}
